import { useTranslations } from 'next-intl';
import ProgressBar from '@monofront/design-system/components/ProgressBar';
import { useShoppingCart } from '@hooks/cart/useShoppingCart';
import {
  ShippingMethodCategory,
  ShippingMethodConfigurationThreshold,
  ShippingMethodTypes,
} from '@monofront/types';
import { max } from 'radash';
import { format } from '@monofront/business/utils';
import { usePreferredShippingMethodWithConfigurations } from '@hooks/usePreferredShippingMethodWithConfiguration';
import { computeFreeDeliveryInfos } from '@monofront/business/services';

const getMaxThreshold = (
  thresholds?: ShippingMethodConfigurationThreshold[],
): number => {
  if (!thresholds) {
    return 0;
  }

  return max(thresholds, (t) => t.threshold)?.threshold || 0;
};

interface IProgressBarByShippingMethodProps {
  UNSAFE_className?: string;
}

const ProgressBarByShippingMethod: React.FC<
  IProgressBarByShippingMethodProps
> = ({ UNSAFE_className = '' }) => {
  const {
    shoppingCart: { totalPrice = 0, hasFreshProduct, hasUltraFreshProduct },
  } = useShoppingCart();
  const tCart = useTranslations('Cart');
  const selectedShippingMethod = usePreferredShippingMethodWithConfigurations();
  let freeDeliveryLimit = 0;
  let sentence;

  if (!selectedShippingMethod?.configurations) {
    return null;
  }

  switch (selectedShippingMethod?.type) {
    case ShippingMethodTypes.COURIER: {
      const currentCategory = hasUltraFreshProduct
        ? ShippingMethodCategory.ULTRA_FRESH
        : hasFreshProduct
        ? ShippingMethodCategory.FRESH
        : ShippingMethodCategory.DEFAULT;

      const freshThresholds = selectedShippingMethod?.configurations?.find(
        ({ category }) => category === currentCategory,
      )?.thresholds;

      freeDeliveryLimit = getMaxThreshold(freshThresholds) / 100;

      sentence = tCart.rich(
        `progressBarByShippingMethod.courier.${currentCategory}`,
        {
          unlocked: totalPrice >= freeDeliveryLimit,
          remainingAmount: format(freeDeliveryLimit - totalPrice),
          b: (children) => (
            <span className="tw-lf-body-bold-md">{children}</span>
          ),
        },
      );

      break;
    }
    case ShippingMethodTypes.HOME:
      freeDeliveryLimit =
        getMaxThreshold(selectedShippingMethod.configurations[0].thresholds) /
        100;

      sentence = tCart.rich('progressBarByShippingMethod.home', {
        unlocked: totalPrice >= freeDeliveryLimit,
        remainingAmount: format(freeDeliveryLimit - totalPrice),
        freeDelivery:
          selectedShippingMethod.configurations[0].aboveAllThresholdsAmount ===
          0,
        deliveryPrice: format(
          selectedShippingMethod.configurations[0].aboveAllThresholdsAmount /
            100,
        ),
        b: (children) => <span className="tw-lf-body-bold-md">{children}</span>,
      });

      break;
    case ShippingMethodTypes.PICKUP:
      freeDeliveryLimit =
        getMaxThreshold(selectedShippingMethod.configurations[0].thresholds) /
        100;

      sentence = tCart.rich('progressBarByShippingMethod.pickup', {
        unlocked: totalPrice >= freeDeliveryLimit,
        remainingAmount: format(freeDeliveryLimit - totalPrice),
        freeDelivery:
          selectedShippingMethod.configurations[0].aboveAllThresholdsAmount ===
          0,
        deliveryPrice: format(
          selectedShippingMethod.configurations[0].aboveAllThresholdsAmount /
            100,
        ),
        b: (children) => <span className="tw-lf-body-bold-md">{children}</span>,
      });

      break;
  }

  const { freeDeliveryProgression } = computeFreeDeliveryInfos(
    totalPrice,
    freeDeliveryLimit,
  );

  return (
    <div
      className={`${UNSAFE_className} tw tw-flex tw-flex-col tw-items-center`}
    >
      <p className="tw-lf-body-regular-md tw-mb-2 tw-text-center">{sentence}</p>
      <ProgressBar currentProgress={freeDeliveryProgression} hasBackground />
    </div>
  );
};

export default ProgressBarByShippingMethod;
